// src/pages/earningsImport/pos/posParser.js

import * as XLSX from "xlsx";

function toLocalISODate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function normalizeDate(raw) {
  if (!raw) return "";

  if (typeof raw === "number") {
    const parsed = XLSX.SSF.parse_date_code(raw);
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(parsed.d).padStart(2, "0")}`;
    }
  }

  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return toLocalISODate(raw);
  }

  const cleaned = String(raw).trim();

  const shortMatch = cleaned.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2})$/);

  if (shortMatch) {
    const months = {
      Jan: "01", Feb: "02", Mar: "03", Apr: "04",
      May: "05", Jun: "06", Jul: "07", Aug: "08",
      Sep: "09", Oct: "10", Nov: "11", Dec: "12"
    };

    const mm = months[shortMatch[2]];

    if (mm) {
      const yyyy =
        Number(shortMatch[3]) >= 50
          ? `19${shortMatch[3]}`
          : `20${shortMatch[3]}`;

      return `${yyyy}-${mm}-${shortMatch[1].padStart(2, "0")}`;
    }
  }

  const ddmmyyyy = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (ddmmyyyy) {
    return `${ddmmyyyy[3]}-${ddmmyyyy[2].padStart(2, "0")}-${ddmmyyyy[1].padStart(2, "0")}`;
  }

  const parsed = new Date(cleaned);
  return Number.isNaN(parsed.getTime()) ? "" : toLocalISODate(parsed);
}

function value(row, names, fallback = "") {
  for (const name of names) {
    if (
      row?.[name] !== undefined &&
      row?.[name] !== null &&
      row?.[name] !== ""
    ) {
      return row[name];
    }
  }

  return fallback;
}

function asText(v) {
  return String(v ?? "").replace(/\.0$/, "").trim();
}

export async function parsePOSFile(file) {
  const buffer = await file.arrayBuffer();

  const workbook = XLSX.read(buffer, {
    type: "array",
    cellDates: true,
  });

  const sheet = workbook.Sheets[workbook.SheetNames[0]];

  // Formatted values preserve things like Auth ID leading zeroes.
  const rows = XLSX.utils.sheet_to_json(sheet, {
    raw: false,
    defval: "",
  });

  // Raw values are used only where Excel scientific notation is a problem.
  const rawRows = XLSX.utils.sheet_to_json(sheet, {
    raw: true,
    defval: "",
  });

  return rows.map((row, index) => {
    const rawRow = rawRows[index] || {};

    const rawDate = value(row, [
      "Txn Date",
      "Transaction Date",
      "Date",
      "date",
    ]);

    const terminal = value(row, [
      "Terminal ID",
      "Terminal Id",
      "Terminal",
      "terminalId",
    ]);

    const amount = value(
      row,
      ["Net Amount", "Amount", "Amount (AED)", "Amt", "Total", "amount"],
      0
    );

    return {
      date: normalizeDate(rawDate),
      terminalId: asText(terminal),
      amount: Number(String(amount ?? "0").replace(/,/g, "")) || 0,

      txnTime: asText(
        value(row, ["Txn Time", "Transaction Time", "Time"])
      ),

      // Keep formatted value so 021493 stays 021493.
      authId: asText(
        value(row, ["Auth ID", "Auth Id", "Authorization ID"])
      ),

      arn: asText(value(row, ["ARN"])),

      // Use raw RRN so 624111144136 does not become 6.24111E+11.
      rrn: asText(
        value(rawRow, ["RRN"], value(row, ["RRN"]))
      ),

      cardNumber: asText(
        value(row, ["Card Number", "Card No", "Card"])
      ),
    };
  });
}